import React, { useLayoutEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { useNavigate } from '@lib/react-router-dom'

import { SelectNewWalletModal } from '@pages/Traders-Room/Balances/Wallets/modals/SelectNewWallet.modal'

import { WalletsExperience } from '@features/wallets'
import { useIntroducingBrokerWalletBreakdown } from '@features/wallets/hooks/useIntroducingBrokerWalletBreakdown'
import { useWalletRecentActivity } from '@features/wallets/hooks/useWalletRecentActivity'
import { useWalletsPageData } from '@features/wallets/hooks/useWalletsPageData'

import { useAccountReadContext } from '@context/AccountContextContext'
import { useModal } from '@context/ModalsContext'

import { TableNoResults } from '@ui/v2/Table/Table'

import { CommonPaths } from '@utils/path'
import { WalletRestrictions, isRestricted } from '@utils/wallet.utils'

import { WalletTypeEnum } from '@model/WalletDto'

import { useFormatNumber } from '@hooks/useFormatNumber'
import { ScrollToIds } from '@hooks/useScrollToElementIds'
import { scrollToTop, useScrollToTop } from '@hooks/useScrollToTop'
import { useWithdrawalPageNavigation } from '@hooks/useWithdrawalPageNavigation'

import { Box } from '@shared/components/Box'
import { Loading } from '@shared/components/Loading/Loading'

export const IBWalletsPage: React.FC = () => {
  useScrollToTop()

  const { t } = useTranslation()
  const location = useLocation()
  const navigate = useNavigate()
  const { account } = useAccountReadContext()
  const { formatMoney } = useFormatNumber()
  const { handleWithdrawalNavigation, walletWithdrawLoadingId } = useWithdrawalPageNavigation()
  const addWalletModal = useModal(SelectNewWalletModal)

  const {
    wallets,
    walletCurrencies,
    selectedWallet,
    selectWallet,
    totalBalance,
    availableCurrencies,
    availableCurrencyIds,
    createWallet,
    isWalletsLoading,
    isTotalBalanceLoading,
    isTotalBalanceError,
    isAvailableCurrenciesLoading,
    isAvailableCurrenciesError,
    isCreatingWallet,
    isError,
  } = useWalletsPageData({ walletType: WalletTypeEnum.IB })

  const walletBreakdown = useIntroducingBrokerWalletBreakdown({
    selectedWalletId: selectedWallet?.id,
    selectedWalletCurrency: selectedWallet?.currency.id,
  })
  const recentActivity = useWalletRecentActivity({
    room: 'ib',
    walletType: WalletTypeEnum.IB,
    walletId: selectedWallet?.id,
    context: 'ib',
    historyPath: CommonPaths.IntroducingBrokerTransactionHistory,
  })

  useLayoutEffect(() => {
    if (location.state?.wallets) scrollToTop()
  }, [location])

  const isInitialLoading = isWalletsLoading || isAvailableCurrenciesLoading

  if (isInitialLoading) {
    return <Loading showLoadingIcon fullWidth isLoading />
  }

  if (isError || (!selectedWallet && wallets.length > 0)) {
    return <TableNoResults title={t('errors.Something went wrong')} />
  }

  const isCreateWalletRestricted = isRestricted(
    WalletRestrictions.CREATE_WALLET,
    account?.restrictions
  )
  const walletList = {
    totalBalance: {
      label: t('Wallet.Total'),
      information: t('Wallet.Total available balance description'),
      value: totalBalance ? formatMoney(totalBalance.balance, totalBalance.currency.id) : undefined,
      isLoading: isTotalBalanceLoading,
      errorMessage: isTotalBalanceError ? t('errors.Something went wrong') : undefined,
      subtitle: t('Wallet.Wallet'),
      currencies: walletCurrencies,
    },
    items: wallets.map((wallet) => ({
      id: wallet.id,
      currencyCode: wallet.currency.id,
      currencyName: wallet.currency.name,
      balance: formatMoney(wallet.balance, wallet.currency.id),
      isCurrent: wallet.id === selectedWallet?.id,
      onSelect: () => selectWallet(wallet.id),
    })),
    currentLabel: t('Current'),
    addCurrency:
      !isAvailableCurrenciesError && availableCurrencies.length > 0
        ? {
            label: t('Add Currency'),
            subtitle: t('Wallet.Wallet'),
            currencies: availableCurrencyIds,
            disabled: isCreateWalletRestricted,
            loading: isCreatingWallet,
            disabledMessage: isCreateWalletRestricted
              ? t('errors.Client is restricted to create a wallet')
              : undefined,
            onSelect: () =>
              addWalletModal.open({
                walletTypes: availableCurrencies,
                onSelect: createWallet,
              }),
          }
        : undefined,
  }

  if (!selectedWallet) {
    return (
      <Box id={ScrollToIds.IBWalletsHeader}>
        <WalletsExperience
          walletList={walletList}
          emptyState={{
            title: t('Wallet.No wallets title'),
            description: t('Wallet.No wallets description'),
          }}
        />
      </Box>
    )
  }

  const isWithdrawDisabled = isRestricted(WalletRestrictions.WITHDRAW, selectedWallet.restrictions)
  const isTransferDisabled = isRestricted(
    WalletRestrictions.TRANSFER_FROM,
    selectedWallet.restrictions
  )

  return (
    <Box id={ScrollToIds.IBWalletsHeader}>
      <WalletsExperience
        currentWallet={{
          balanceLabel: t('Wallet.Available Balance'),
          balance: formatMoney(selectedWallet.balance, selectedWallet.currency.id),
          currencyCode: selectedWallet.currency.id,
          walletNumber: selectedWallet.name,
          isBreakdownLoading: walletBreakdown.isLoading,
          breakdownErrorMessage: walletBreakdown.isError
            ? t('errors.Something went wrong')
            : undefined,
          information: (
            <>
              <Box>
                <strong>{t('Available balance')}</strong>
                {` – ${t('Wallet.IB available balance description')}`}
              </Box>
              <Box>
                <strong>{t('IB.Incoming Reserved')}</strong>
                {` – ${t('Wallet.IB incoming reserved description')}`}
              </Box>
              <Box>
                <strong>{t('IB.Outgoing Reserved')}</strong>
                {` – ${t('Wallet.IB outgoing reserved description')}`}
              </Box>
              <Box>
                <strong>{t('IB.All Time Reward')}</strong>
                {` – ${t('Wallet.IB all time reward description')}`}
              </Box>
              <Box>{t('Wallet.IB wallet description')}</Box>
            </>
          ),
          copyWalletNumberLabel: t('Copy wallet number'),
          breakdown: [
            {
              id: 'incoming-reserved',
              label: t('IB.Incoming Reserved'),
              value: formatMoney(walletBreakdown.incomingReserved, selectedWallet.currency.id),
            },
            {
              id: 'outgoing-reserved',
              label: t('IB.Outgoing Reserved'),
              value: formatMoney(selectedWallet.reserved, selectedWallet.currency.id),
            },
            {
              id: 'all-time-reward',
              label: t('IB.All Time Reward'),
              value: formatMoney(walletBreakdown.allTimeReward, selectedWallet.currency.id),
            },
          ],
          actions: {
            items: [
              {
                kind: 'withdraw',
                label: t('Withdraw'),
                disabled: isWithdrawDisabled,
                loading: walletWithdrawLoadingId === selectedWallet.id,
                onClick: () => handleWithdrawalNavigation({ walletId: selectedWallet.id }),
              },
              {
                kind: 'transfer',
                label: t('Transfer'),
                disabled: isTransferDisabled,
                onClick: () =>
                  navigate(`${CommonPaths.IntroducingBrokerWallets}/${selectedWallet.id}/transfer`),
              },
            ],
            primaryAction: 'withdraw',
            mobileOrder: ['withdraw', 'transfer'],
            menuLabel: t('Wallet actions'),
            actionUnavailableMessage: t('Wallet.Action unavailable'),
            walletUnavailableMessage: t('Wallet.Transactions unavailable'),
          },
        }}
        walletList={walletList}
        recentActivity={recentActivity}
      />
    </Box>
  )
}
